/**
 * The scan, end to end.
 *
 * Stages, in order, each in its own module:
 *
 *   quality -> detect -> correct -> preprocess -> OCR (xN)
 *     -> parse -> consensus -> validate -> confidence
 *
 * Two properties matter more than any individual stage:
 *
 *   1. NOTHING LEAVES THE DEVICE. The only network requests this feature can
 *      make are for its own runtime and model, from this origin, and only until
 *      the service worker has them. There is no upload path in this file or any
 *      module it imports — no fetch, no FormData, no beacon.
 *
 *   2. IT CAN GIVE UP. Every stage can decide the photo is not good enough, and
 *      saying "take another one" is always allowed. That is the whole reliability
 *      posture: a wrong nutrition value is worse than no value, because the user
 *      cannot tell it is wrong.
 */

import { mergeLabels } from './consensus';
import { parseLabel } from './parser';
import { applyIssues, validateLabel } from './validate';
import type {
  DetectedRegion,
  NutritionLabel,
  OCREngine,
  OCRResult,
  ScanProgress,
  ScanResult,
} from './types';
import type { PrepareError, PrepareResponse } from './image.worker';

export interface ScanOptions {
  /** Called on every stage change so the UI can narrate. */
  onProgress?: (progress: ScanProgress) => void;
  /** Aborts between stages and inside the OCR loop. */
  signal?: AbortSignal;
  /** Skips detection when the user has cropped by hand. */
  manualRegion?: DetectedRegion | null;
  /** Collects intermediate state for the debug view. Off in production. */
  debug?: boolean;
  /**
   * How many preprocessing variants to read.
   *
   * Each is a full OCR pass, so this is the main quality/time dial: more passes
   * mean stronger consensus and a slower scan. Tuned down on weak devices.
   */
  maxPasses?: number;
}

export class ScanAbortedError extends Error {
  constructor() {
    super('Scan cancelled');
    this.name = 'ScanAbortedError';
  }
}

export class ScanQualityError extends Error {
  constructor(
    message: string,
    readonly result: ScanResult,
  ) {
    super(message);
    this.name = 'ScanQualityError';
  }
}

const STAGE_NOTES: Record<ScanProgress['stage'], string> = {
  assessing: 'Checking the photo…',
  detecting: 'Finding the label…',
  correcting: 'Straightening the label…',
  preprocessing: 'Preparing the image…',
  loading_model: 'Loading the reader…',
  reading: 'Reading the text…',
  parsing: 'Understanding the values…',
  validating: 'Checking the numbers add up…',
  done: 'Done',
};

/**
 * Run a scan.
 *
 * The image worker is passed in rather than constructed here so it can be kept
 * alive across scans — the worker itself is cheap, but re-creating it on every
 * retake adds a startup pause exactly when the user is already frustrated.
 */
export async function runScan(
  image: ImageData,
  engine: OCREngine,
  worker: Worker,
  options: ScanOptions = {},
): Promise<ScanResult> {
  const started = Date.now();
  const report = (stage: ScanProgress['stage'], fraction: number): void => {
    options.onProgress?.({ stage, fraction, note: STAGE_NOTES[stage] });
  };
  const checkAborted = (): void => {
    if (options.signal?.aborted) throw new ScanAbortedError();
  };

  checkAborted();
  report('assessing', 0.05);

  const prepared = await prepare(worker, image, options.manualRegion ?? null, options.signal);
  checkAborted();

  report('preprocessing', 0.2);

  /*
   * A photo that failed the quality gate stops here.
   *
   * It returns a result rather than throwing a bare error so the UI has
   * something to show: which checks failed, and the one thing to do about it.
   */
  if (!prepared.quality.usable || prepared.variants.length === 0) {
    const empty: NutritionLabel = {
      servingSize: null,
      servingsPerContainer: null,
      basis: 'per_100g',
      nutrients: {},
      confidence: 0,
      warnings: [prepared.quality.advice ?? 'The photo was not clear enough to read.'],
      format: 'none',
    };
    throw new ScanQualityError(prepared.quality.advice ?? 'Photo not clear enough', {
      label: empty,
      quality: prepared.quality,
      issues: [],
      elapsedMs: Date.now() - started,
    });
  }

  report('loading_model', 0.25);
  await engine.initialize((fraction) => {
    options.onProgress?.({
      stage: 'loading_model',
      fraction: 0.25 + fraction * 0.1,
      note: 'Loading the reader…',
    });
  });
  checkAborted();

  const passes: OCRResult[] = [];
  const labels: NutritionLabel[] = [];
  const wanted = Math.max(1, Math.min(options.maxPasses ?? 4, prepared.variants.length));

  for (let i = 0; i < wanted; i++) {
    checkAborted();
    const variant = prepared.variants[i]!;
    report('reading', 0.35 + (i / wanted) * 0.45);

    let result: OCRResult;
    try {
      result = await engine.recognize(variant.image);
    } catch {
      // One variant failing is not the scan failing — the others may well
      // read fine, and consensus is designed to work with fewer passes.
      continue;
    }
    result.variant = variant.name;
    passes.push(result);

    const { label } = parseLabel(result);
    labels.push(label);

    /*
     * Stop early when the answer is already settled.
     *
     * If two passes agree on all four macros with high confidence, a third and
     * fourth cost seconds on a phone and change nothing. The consensus rule is
     * unchanged — this only skips work that could not alter the outcome.
     */
    if (i >= 1 && settled(labels)) break;
  }

  checkAborted();
  report('parsing', 0.85);

  if (labels.length === 0) {
    const empty: NutritionLabel = {
      servingSize: null,
      servingsPerContainer: null,
      basis: 'per_100g',
      nutrients: {},
      confidence: 0,
      warnings: ['No text could be read from this photo.'],
      format: 'none',
    };
    throw new ScanQualityError('Nothing could be read', {
      label: empty,
      quality: prepared.quality,
      issues: [],
      elapsedMs: Date.now() - started,
    });
  }

  const merged = mergeLabels(labels);

  report('validating', 0.93);
  const issues = validateLabel(merged);
  const label = applyIssues(merged, issues);

  report('done', 1);

  const result: ScanResult = {
    label,
    quality: prepared.quality,
    issues,
    elapsedMs: Date.now() - started,
  };

  if (options.debug) {
    const { rows } = parseLabel(passes[0]!);
    result.debug = {
      region: prepared.region,
      quad: null,
      variants: prepared.variants.map((v) => ({
        name: v.name,
        width: v.image.width,
        height: v.image.height,
      })),
      passes,
      rows: rows.map((r) => ({
        label: r.label,
        amount: r.amount === null ? '—' : `${r.amount}${r.unit ?? ''}`,
        dailyValue: r.dailyValue === null ? '—' : `${r.dailyValue}%`,
      })),
    };
  }

  return result;
}

/** All four macros read cleanly and identically by every pass so far. */
function settled(labels: NutritionLabel[]): boolean {
  if (labels.length < 2) return false;
  const keys = ['calories', 'protein', 'totalCarbohydrate', 'totalFat'] as const;
  return keys.every((key) => {
    const values = labels.map((l) => l.nutrients[key]);
    if (values.some((v) => v === undefined || v.value === null)) return false;
    const first = values[0]!;
    if (first.confidence < 0.7) return false;
    return values.every((v) => v!.value === first.value && v!.unit === first.unit);
  });
}

let nextRequestId = 1;

/** One request/response round trip with the image worker. */
function prepare(
  worker: Worker,
  image: ImageData,
  manualRegion: DetectedRegion | null,
  signal?: AbortSignal,
): Promise<PrepareResponse> {
  const id = nextRequestId++;

  return new Promise<PrepareResponse>((resolve, reject) => {
    const onMessage = (event: MessageEvent<PrepareResponse | PrepareError>): void => {
      if (event.data.id !== id) return;
      cleanup();
      if (event.data.type === 'error') reject(new Error(event.data.message));
      else resolve(event.data);
    };
    const onError = (): void => {
      cleanup();
      reject(new Error('Image processing failed'));
    };
    const onAbort = (): void => {
      cleanup();
      reject(new ScanAbortedError());
    };
    const cleanup = (): void => {
      worker.removeEventListener('message', onMessage as EventListener);
      worker.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
    };

    worker.addEventListener('message', onMessage as EventListener);
    worker.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort);

    worker.postMessage({ type: 'prepare', id, image, manualRegion });
  });
}

/** Start the image worker. Vite bundles this without a CDN. */
export function createImageWorker(): Worker {
  return new Worker(new URL('./image.worker.ts', import.meta.url), { type: 'module' });
}
